import { api } from './client';
import {
  AdminCreateModelDto,
  AdminFlagsDto,
  AdminModelView,
  AdminModelPhotoPruneRequest,
  AdminModelPhotoPruneResponse,
  AdminModelPhotoRequest,
  AdminModelPhotoView,
  AdminPatchModelDto,
  CreatorPageConfig,
  AdminTagsDeltaDto,
  AdminTagsReplaceDto,
  BulkUpdateDto,
  PageResponseAdminModelView,
  AdminPromoteSummary,
  UpdatePriorityBiasDto,
  PpcOrderItem
} from './types';

export interface ModelsQuery {
  q?: string;
  username?: string;
  tag?: string;
  blacklisted?: boolean;
  deleted?: boolean;
  promote?: boolean;
  isPromo?: boolean;
  priorityMin?: number;
  priorityMax?: number;
  manualBiasMin?: number;
  manualBiasMax?: number;
  page?: number;
  limit?: number;
  order?: string;
}

export const listModels = async (query: ModelsQuery): Promise<PageResponseAdminModelView> => {
  const { data } = await api.get<PageResponseAdminModelView>('/admin/models', {
    params: query
  });
  return data;
};

export const getModel = async (id: number): Promise<AdminModelView> => {
  const { data } = await api.get<AdminModelView>(`/admin/models/${id}`);
  return data;
};

export const getModelByUsername = async (modelUsername: string, source?: 'OF' | 'OT'): Promise<AdminModelView> => {
  const { data } = await api.get<AdminModelView>(`/admin/models/by-username/${encodeURIComponent(modelUsername)}`, {
    params: source ? { source } : undefined
  });
  return data;
};

export const createModel = async (payload: AdminCreateModelDto): Promise<AdminModelView> => {
  const { data } = await api.post<AdminModelView>('/admin/models', payload);
  return data;
};

export const patchModel = async (id: number, payload: AdminPatchModelDto): Promise<AdminModelView> => {
  const { data } = await api.patch<AdminModelView>(`/admin/models/${id}`, payload);
  return data;
};

export const patchFlags = async (id: number, payload: AdminFlagsDto): Promise<AdminModelView> => {
  const { data } = await api.patch<AdminModelView>(`/admin/models/${id}/flags`, payload);
  return data;
};

export const listModelPhotos = async (id: number): Promise<AdminModelPhotoView[]> => {
  const { data } = await api.get<AdminModelPhotoView[]>(`/admin/models/${id}/photos`);
  return data;
};

export const addModelPhoto = async (id: number, payload: AdminModelPhotoRequest): Promise<AdminModelPhotoView> => {
  const { data } = await api.post<AdminModelPhotoView>(`/admin/models/${id}/photos`, payload);
  return data;
};

export const selectModelPhoto = async (id: number, photoId: number): Promise<AdminModelPhotoView> => {
  const { data } = await api.put<AdminModelPhotoView>(`/admin/models/${id}/photos/${photoId}/select`);
  return data;
};

export const deleteModelPhoto = async (
  id: number,
  photoId: number
): Promise<{ id: number; modelId: number; fileName: string; deleted: boolean; physicalDeleteAllowed: boolean; physicalDeleted: boolean }> => {
  const { data } = await api.delete(`/admin/models/${id}/photos/${photoId}`);
  return data;
};

export const pruneLegacySyncModelPhotos = async (
  id: number,
  payload: AdminModelPhotoPruneRequest
): Promise<AdminModelPhotoPruneResponse> => {
  const { data } = await api.post<AdminModelPhotoPruneResponse>(
    `/admin/models/${id}/photos/prune-legacy-sync`,
    payload
  );
  return data;
};

export const replaceTags = async (id: number, payload: AdminTagsReplaceDto): Promise<void> => {
  await api.put(`/admin/models/${id}/tags`, payload);
};

export const getCreatorPageConfig = async (id: number): Promise<CreatorPageConfig> => {
  const { data } = await api.get<CreatorPageConfig>(`/admin/models/${id}/creator-page`);
  return data;
};

export const saveCreatorPageConfig = async (id: number, payload: CreatorPageConfig): Promise<CreatorPageConfig> => {
  const { data } = await api.put<CreatorPageConfig>(`/admin/models/${id}/creator-page`, payload);
  return data;
};

export const addTags = async (id: number, payload: AdminTagsDeltaDto): Promise<void> => {
  await api.post(`/admin/models/${id}/tags`, payload);
};

export const removeTags = async (id: number, payload: AdminTagsDeltaDto): Promise<void> => {
  await api.delete(`/admin/models/${id}/tags`, { data: payload });
};

export const getPromoteSummary = async (username?: string): Promise<AdminPromoteSummary> => {
  const params = username ? { username } : undefined;
  const { data } = await api.get<AdminPromoteSummary>('/admin/models/promote/summary', { params });
  return data;
};

export const setModelPriorityBias = async (id: number, payload: UpdatePriorityBiasDto, recompute = true): Promise<void> => {
  await api.patch(`/admin/ranking/models/${id}`, payload, { params: { recompute } });
};

export const setTagModelPriorityBias = async (
  tagId: number,
  id: number,
  payload: UpdatePriorityBiasDto,
  recompute = true
): Promise<void> => {
  await api.patch(`/admin/ranking/tags/${tagId}/models/${id}`, payload, { params: { recompute } });
};

export const bulkModelPriorityBias = async (payload: BulkUpdateDto, recompute = true): Promise<void> => {
  await api.post('/admin/ranking/models/bulk', payload, { params: { recompute } });
};

export const bulkTagModelPriorityBias = async (tagId: number, payload: BulkUpdateDto, recompute = true): Promise<void> => {
  await api.post(`/admin/ranking/tags/${tagId}/models/bulk`, payload, { params: { recompute } });
};

// Batch update ppc_order for multiple models
export const batchUpdatePpcOrder = async (
  items: PpcOrderItem[]
): Promise<{ updated: number }> => {
  const { data } = await api.post<{ updated: number }>('/admin/models/batch-ppc-order', items);
  return data;
};

// Batch update flags for multiple models
export const batchUpdateFlags = async (
  ids: number[],
  flags: { promote?: boolean; blacklisted?: boolean }
): Promise<{ updated: number }> => {
  const { data } = await api.post<{ updated: number }>('/admin/models/batch-flags', {
    ids,
    ...flags
  });
  return data;
};

// List PPC models (using monetizationGroup filter)
export const listPpcModels = async (): Promise<PageResponseAdminModelView> => {
  const { data } = await api.get<PageResponseAdminModelView>('/admin/models', {
    params: { monetizationGroup: 'PPC', limit: 100, order: 'ppcOrder.asc' }
  });
  return data;
};
